<template>
  <div class="relative flex flex-col">
    <slot name="back-button" />
    <div class="xl:flex">
      <div class="mb-2 xl:mb-0">
        <Heading color="blue" size="large" tag="h1">
          <CommonLabel path="BWF/shop/account/orderDetails/orderDetailsHeadline" />
        </Heading>
        <Heading
          class="flex flex-wrap items-center gap-1 gap-x-4 xl:gap-4"
          size="large"
          tag="h2"
          weight="normal"
          data-no-tracking
        >
          <span>
            <CommonLabel path="BWF/shop/account/orderDetails/orderNumber" />
            {{ order?.orderNumber }}
          </span>
          <AccountOrderStateLabel :state="order?.stateMachineState" />
        </Heading>
      </div>
      <div class="mb-6 ml-auto flex place-self-end xl:mb-0">
        <TextSelectField
          ref="orderActionSelectField"
          v-model="selectedMenuOption"
          :label="orderActions"
          :options="menuOptions"
        >
          <CommonLabel class="text-sm" path="BWF/shop/account/orderDetails/orderActions" />
        </TextSelectField>
      </div>
    </div>

    <Divider class="hidden xl:mb-4 xl:mt-2 xl:block" />

    <div class="flex flex-col gap-2 xl:gap-4">
      <OrderDetailsHeader />

      <Divider class="hidden xl:block" />

      <OrderDetailsAddresses />

      <Divider class="mt-4 xl:mt-0" />

      <div class="flex flex-col gap-4">
        <div class="text-xl font-medium">
          <CommonLabel path="BWF/shop/account/orderDetails/orderDetails" />
        </div>
        <OrderLineItems :order="order" class="mt-4" />

        <Divider class="mb-6 mt-4 w-full" />

        <OrderLineItemsPrice :order="order" class="mt-4" />
      </div>

      <template v-if="invoiceDownloads?.length">
        <Divider class="mb-6 mt-4 xl:my-0" />
        <div class="flex flex-col gap-4 pb-4 xl:pb-6">
          <div class="mb-2 text-base font-medium xl:my-4 xl:text-xl">
            <CommonLabel path="BWF/shop/account/orderDetails/receiptInformations" />
          </div>
          <a
            v-for="invoiceDownload in invoiceDownloads"
            :key="invoiceDownload.id"
            :href="invoiceDownload.url"
            class="text-link flex items-end gap-2 text-base no-underline"
            download
            target="_blank"
          >
            <span class="truncate leading-none no-underline">
              {{ `${invoiceDownload.name}_${invoiceDownload.number}` }}
            </span>
            <SvgIcon class="h-5 w-5" name="Download" />
          </a>
        </div>
      </template>

      <template v-if="containsReturned || containsRefund">
        <Divider class="mb-6 mt-4 xl:my-0" />
        <div class="mt-4 flex flex-col">
          <div class="text-xl font-medium">
            <CommonLabel v-if="containsRefund" path="BWF/shop/account/orderDetails/refundedItems" />
            <CommonLabel v-else path="BWF/shop/account/orderDetails/returnedItems" />
          </div>
          <OrderLineItems :order="order" :show-returns="true" class="mt-4" />
        </div>
      </template>

      <Divider v-if="order?.extensions?.returns?.length" class="mb-6 mt-4 xl:my-0" />

      <OrderReturnDetails v-if="order?.extensions?.returns?.length" :order="order" />

      <Divider v-if="order?.deliveries?.length" class="mb-6 mt-4 xl:my-0" />

      <div v-if="order?.deliveries?.length" class="pb-4 xl:pb-6">
        <div class="mb-2 text-base font-medium xl:my-4 xl:text-xl">
          <CommonLabel path="BWF/shop/account/orderDetails/trackingInformations" />
        </div>

        <div
          v-for="delivery in order?.deliveries"
          :key="delivery?.id"
          class="grid w-full grid-cols-2 xl:w-1/2"
        >
          <div class="text-sm font-medium">
            <CommonLabel path="BWF/shop/account/orderDetails/trackingCarrier"></CommonLabel>
          </div>
          <div>{{ delivery?.shippingMethod?.name }}</div>
          <div
            class="col-span-full text-sm font-medium md:col-span-1 md:col-start-2 md:row-start-1"
          >
            <CommonLabel path="BWF/shop/account/orderDetails/trackingId" />
          </div>
          <div>
            <CommonLabel
              v-if="!delivery?.trackingCodes?.length"
              path="BWF/shop/account/orderDetails/noTrackingId"
            />
            <a
              v-for="trackingCode in delivery?.trackingCodes"
              v-else
              :key="trackingCode"
              :href="`${delivery?.shippingMethod?.trackingUrl?.replace('%s', trackingCode)}`"
              class="text-link mb-3 flex items-end text-base no-underline"
              target="_blank"
            >
              <span class="leading-none no-underline">
                {{ trackingCode }}
              </span>
              &nbsp;
              <SvgIcon class="h-4 w-4 shrink-0" name="External-Link" />
            </a>
          </div>
        </div>
      </div>

      <Divider class="mb-6 mt-4 xl:my-0" />
      <CommonLabel path="BWF/shared/vatFootnote" />
    </div>
  </div>
</template>

<script setup>
import { Divider } from "~/components/clarity-ui/Divider";
import { Heading } from "~/components/clarity-ui/Heading";
import useCommonLabels from "~/composables/useCommonLabels";
import { useShopConfiguration } from "~/composables/shop/useShopConfiguration";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import OrderLineItems from "~/templates/components/shop/account/OrderLineItems.vue";
import OrderReturnDetails from "~/templates/components/shop/account/OrderReturnDetails.vue";
import OrderDetailsHeader from "~/templates/components/shop/account/OrderDetailsHeader.vue";
import OrderDetailsAddresses from "~/templates/components/shop/account/OrderDetailsAddresses.vue";
import OrderLineItemsPrice from "~/templates/components/shop/account/OrderLineItemsPrice.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import TextSelectField from "~/templates/elements/form/TextSelectField.vue";
import {
  containsCreditLineItems,
  ORDER_DOCUMENT_TYPE_CREDIT_NOTE,
  ORDER_DOCUMENT_TYPE_INVOICE,
} from "~/utils/helper/shop/order";

const props = defineProps(["order"]);

const { cleanedShopwareEndpoint } = useShopConfiguration();
const { getLabels } = useCommonLabels();
const { startReturn, startCancellation, startWarranty, orderActions } = getLabels(
  "BWF/shop/account/orderDetails",
  ["startReturn", "startCancellation", "startWarranty", "orderActions"],
);

const containsReturned = computed(() => props.order?.extensions?.returns?.length > 0);
const containsRefund = computed(() => containsCreditLineItems(props.order?.lineItems));

// all deliveries need to be open to be cancellable & the order needs a SAP ordernumber
const isCancellable = computed(() => props.order?.extensions?.isCancellationAllowed?.value);

// if containsReturned will be handled after selection. user will be send to customer service
const isReturnable = computed(
  () => props.order?.extensions?.isReturnAllowed?.value || containsReturned.value,
);

const isWarranty = computed(() => props.order?.extensions?.isWarrantyAllowed?.value);

const buildMenuOptions = async () => [
  {
    label: startReturn,
    value: "return",
    disabled: !isReturnable.value,
  },
  {
    label: startCancellation,
    value: "cancel",
    disabled: !isCancellable.value,
  },
  {
    label: startWarranty,
    value: "warranty",
    disabled: !isWarranty.value,
  },
];

const invoiceDownloads = computed(() => {
  const invoiceTypes = [ORDER_DOCUMENT_TYPE_INVOICE, ORDER_DOCUMENT_TYPE_CREDIT_NOTE];
  return props.order?.documents
    ?.filter((document) =>
      invoiceTypes.includes(document.documentType?.technicalName?.toLowerCase()),
    )
    .map((document) => {
      return {
        url: `${cleanedShopwareEndpoint.value}/store-api/brita/document/download/${document.id}/${document.deepLinkCode}`,
        id: document.id,
        number: document.config.documentNumber,
        name: document.documentType.name,
      };
    });
});

const menuOptions = ref([]);

const selectedMenuOption = ref("");

const orderActionSelectField = ref(null);

const emit = defineEmits([
  "order-action-cancel",
  "order-action-return",
  "order-action-warranty",
  "order-action-service",
]);

watch(selectedMenuOption, (option) => {
  switch (option) {
    case "return":
      if (containsReturned.value) {
        emit("order-action-service");
      } else {
        emit("order-action-return");
      }
      break;
    case "warranty":
      emit("order-action-warranty");
      break;
    case "cancel":
      emit("order-action-cancel");
      break;
  }
  // reset options
  orderActionSelectField.value?.reset();
});

menuOptions.value = await buildMenuOptions();
</script>
